import React, { useEffect, useState } from "react";
import MainApp from "./components/MainApp";
import Login from "./components/Login";
import {
  clearAllStorage,
  clearExpiredSession,
  initializeSession,
  validateSession,
  getStoreId,
  setStoreId,
  getEcwidAuth,
  setEcwidAuth,
  setApiKey,
  setSettings
} from "./utils/storage";
import "./App.css";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const existingClarity = document.getElementById("clarity-script");
    if (!existingClarity) {
      const clarityScript = document.createElement("script");
      clarityScript.id = "clarity-script";
      clarityScript.type = "text/javascript";
      clarityScript.text = `(function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "v36ses9m7l");`;
      document.head.appendChild(clarityScript);
    }

    const existingZohoInline = document.getElementById("zoho-salesiq-inline");
    if (!existingZohoInline) {
      const zohoInline = document.createElement("script");
      zohoInline.id = "zoho-salesiq-inline";
      zohoInline.type = "text/javascript";
      zohoInline.text = "window.$zoho=window.$zoho || {};window.$zoho.salesiq=window.$zoho.salesiq||{ready:function(){}};";
      document.head.appendChild(zohoInline);
    }

    const existingZohoWidget = document.getElementById("zsiqscript");
    if (!existingZohoWidget) {
      const zohoWidget = document.createElement("script");
      zohoWidget.id = "zsiqscript";
      zohoWidget.type = "text/javascript";
      zohoWidget.async = true;
      zohoWidget.src = "https://salesiq.zohopublic.in/widget?wc=siq61e7d4af0c7813450f1b1656d673d5949c4876f3a5515d6a15c753b77d7223e9";
      document.head.appendChild(zohoWidget);
    }
  }, []);  

  // Load API key and settings from backend
  const loadApiKeyAndSettings = async (storeId) => {
    try {
      console.log("Loading API key and settings for store:", storeId);

      // Get the hash_site_id from sessionStorage
      const hashSiteId = getStoreId();
      if (!hashSiteId) {
        console.log("No hash_site_id found, skipping settings load");
        return;
      }

      // Fetch settings from backend
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const settingsResponse = await fetch(`${apiUrl}/api/settings?id=${hashSiteId}`);
      console.log("Settings response status:", settingsResponse.status);

      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        console.log("Settings data received:", settingsData);

        if (settingsData.success && settingsData.settings) {
          const backendSettings = settingsData.settings;

          // Store API key in sessionStorage if available
          if (backendSettings.apiKey) {
            setApiKey(backendSettings.apiKey);
            console.log("API key loaded into sessionStorage");
          }

          // Store settings in sessionStorage
          setSettings(backendSettings);
          console.log("Settings loaded into sessionStorage");
        }
      } else {
        console.error("Settings response not ok:", settingsResponse.status);
      }
    } catch (error) {
      console.error("Failed to load API key and settings:", error);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const id = new URLSearchParams(window.location.search).get("id");
      const storeId = new URLSearchParams(window.location.search).get("store_id");
      const token = new URLSearchParams(window.location.search).get("token");

      console.log("Id:", id);
      console.log("Store id:", storeId);
      console.log("Token:", token);

      // Check if we have stored authentication data
      const storedHashId = await getStoreId(id);
      console.log("Stored hash id:", storedHashId);
      const storedAuth = getEcwidAuth();
      console.log("Stored auth:", storedAuth);

      // Check if session is valid
      // if (!validateSession()) {
      //   console.log("Session expired or invalid, clearing stored data");
      //   setLoading(false);
      //   return;
      // }

      if (id) {
        // New flow: using hash ID from URL
        getStoreId(id).then(siteData => {
          if (siteData) {
            console.log("Site data:", siteData);
            // Store hash_id in sessionStorage (non-sensitive)
            setStoreId(id);

            // Store sensitive data in sessionStorage (cleared when tab closes)
            setEcwidAuth({
              store_id: siteData.store_id,
              access_token: siteData.access_token
            });

            // Initialize session with timestamp
            initializeSession();

            // Load API key and settings from backend
            loadApiKeyAndSettings(siteData.store_id).then(() => {
              setIsAuthenticated(true);
              fetchStoreInfo(siteData.store_id, siteData.access_token);
            });
          } else {
            setLoading(false);
          }
        });
      } else if (storeId && token) {
        // Legacy flow: direct store_id and token from URL
        // Store sensitive data in sessionStorage
        setEcwidAuth({
          store_id: storeId,
          access_token: token
        });

        // Initialize session with timestamp
        initializeSession();

        // Load API key and settings from backend
        loadApiKeyAndSettings(storeId).then(() => {
          setIsAuthenticated(true);
          fetchStoreInfo(storeId, token);
        });
      } else if (storedAuth && storedHashId) {
        // Use stored authentication data
        try {
          if (storedAuth.store_id && storedAuth.access_token) {
            // Load API key and settings from backend
            loadApiKeyAndSettings(storedAuth.store_id).then(() => {
              setIsAuthenticated(true);
              fetchStoreInfo(storedAuth.store_id, storedAuth.access_token);
            });
          } else {
            setLoading(false);
          }
        } catch (error) {
          console.error("Failed to parse stored auth data:", error);
          clearExpiredSession();
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  const getStoreId = async (id) => {
    try {
      console.log("Fetching store id for:", id);
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      console.log("API URL:", apiUrl);
      const response = await fetch(`${apiUrl}/api/store-id?id=${id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.store_id && data.access_token) {
          return data;
        }
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch store data:", error);
      return null;
    }
  };

  const fetchStoreInfo = async (storeId, token) => {
    try {
      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      console.log("API URL:", apiUrl);
      console.log("Fetching store info for:", { storeId, hasToken: !!token });
      const response = await fetch(`${apiUrl}/api/store-info?storeId=${storeId}&token=${token}`);

      if (response.ok) {
        const data = await response.json();
        console.log("Store info received:", data);
        setStoreInfo(data);
      } else {
        const errorData = await response.json();
        console.error("Store info API error:", {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
      }
    } catch (error) {
      console.error("Failed to fetch store info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear all stored data using utility function
    clearAllStorage();

    setIsAuthenticated(false);
    setStoreInfo(null);
    window.location.href = "/install";
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading your store...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <MainApp
      storeInfo={storeInfo}
      onLogout={handleLogout}
    />
  );
}

export default App;
